import React, { useMemo, useState, useCallback } from 'react';
import { Table, Tooltip, Tag, Switch, Badge, Empty, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TreeTableProps, TransformedTreeNode } from './types';
import { transformTreeData, filterTreeToSelected, collectAllKeys, filterTreeBySearch } from './utils/treeTransformers';
import { useTreeSelectionStore } from '../../../store/tree-table-store';
import { WORKFLOW_TAGS } from '../../../constants/workflow-tags';
import './TreeTable.scss';

const TreeTable: React.FC<TreeTableProps> = ({ data, loading = false, disabled = false }) => {
  const { toggleSelection, selectedNodes, setOriginalTreeData } = useTreeSelectionStore();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Transform data and get default expanded keys
  const dataString = JSON.stringify(data);
  const { transformedData, defaultExpandedKeys } = useMemo(() => {
    return transformTreeData(data.root);
  }, [dataString]);

  // Create a stable selected keys set to avoid unnecessary recalculations
  const selectedKeysSet = useMemo(() => {
    return new Set(selectedNodes.keys());
  }, [selectedNodes]);

  // Apply filters: first selection filter, then search filter
  const displayData = useMemo(() => {
    let filtered: TransformedTreeNode | null = transformedData;
    
    // Apply selection filter if toggle is on
    if (showSelectedOnly) {
      filtered = filterTreeToSelected(filtered, selectedKeysSet);
      if (!filtered) return null;
    }
    
    // Apply search filter if search term exists
    if (searchTerm && filtered) {
      filtered = filterTreeBySearch(filtered, searchTerm);
    }
    
    return filtered;
  }, [transformedData, showSelectedOnly, selectedKeysSet, searchTerm]);

  // Track previous filter state to detect transitions
  const prevFiltersActive = React.useRef(false);
  const isInitialMount = React.useRef(true);

  // Auto-expand nodes based on filters
  React.useEffect(() => {
    const filtersActive = showSelectedOnly || !!searchTerm;

    if (filtersActive && displayData) {
      // Auto-expand all when filtering is active
      const allKeys = collectAllKeys(displayData);
      setExpandedRowKeys(Array.from(allKeys));
    } else if (prevFiltersActive.current && !filtersActive) {
      // Only reset when transitioning FROM filtered TO unfiltered
      setExpandedRowKeys(defaultExpandedKeys);
    } else if (isInitialMount.current && !filtersActive) {
      // Set default expansion on initial mount
      setExpandedRowKeys(defaultExpandedKeys);
    }

    prevFiltersActive.current = filtersActive;
    isInitialMount.current = false;
  }, [showSelectedOnly, searchTerm, displayData, defaultExpandedKeys]);

  // Store original tree data for API export - only on mount
  React.useEffect(() => {
    setOriginalTreeData(data.root);
  }, []);

  // Memoize selected row keys array
  const selectedRowKeys = useMemo(() => {
    return Array.from(selectedNodes.keys());
  }, [selectedNodes]);

  // Memoize callback functions
  const handleSelect = useCallback((record: TransformedTreeNode, selected: boolean) => {
    if (record.isSelectable && !disabled) {
      toggleSelection(record);
    }
  }, [disabled, toggleSelection]);

  const handleToggleChange = useCallback((checked: boolean) => {
    setShowSelectedOnly(checked);
  }, []);

  const getCheckboxProps = useCallback((record: TransformedTreeNode) => ({
    disabled: !record.isSelectable || disabled,
  }), [disabled]);

  const getRowClassName = useCallback((record: TransformedTreeNode): string => {
    const classes: string[] = [];
    
    // Add non-selectable class for dimension nodes
    if (!record.isSelectable) {
      classes.push('tree-row-not-selectable');
    }
    
    // Add special classes when in filtered view
    if (showSelectedOnly) {
      const isSelected = selectedNodes.has(record.key);
      if (isSelected) {
        classes.push('tree-row-selected-item');
      } else {
        // This is a parent context node (not selected, shown for hierarchy)
        classes.push('tree-row-parent-context');
      }
    }
    
    return classes.join(' ');
  }, [showSelectedOnly, selectedNodes]);

  // Memoize empty state renderer
  const renderEmptyState = useCallback(() => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>No nodes selected</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
            Select nodes from the tree to view them here
          </div>
        </div>
      }
    >
    </Empty>
  ), [showSelectedOnly, handleToggleChange, disabled, loading]);

  // Column definitions - memoized to prevent recreation
  const columns: ColumnsType<TransformedTreeNode> = useMemo(() => [
    {
      title: WORKFLOW_TAGS.PPM.label,
      dataIndex: 'name',
      key: 'name',
      width: '90%',
      render: (name: string, record: TransformedTreeNode) => {
        return (
          <div
            className="tree-node-name"
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span className="tree-node-text">{name}</span>
            {record.metadata?.tag && (
              <Tag color={record.metadata.tag.color}>{record.metadata.tag.label}</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Variance',
      dataIndex: 'displayValue',
      key: 'variance',
      align: 'right',
      width: 200,
      render: (displayValue: string | undefined, record: TransformedTreeNode) => {
        if (!displayValue) {
          return null;
        }

        const value = parseFloat(displayValue);
        const isNegative = value < 0;

        return (
          <div style={{ display: 'inline-block' }}>
            <Tooltip title={`Full value: ${record.rawValue}`} placement="left">
              <Tag color={isNegative ? 'geekblue' : 'green'}>
                {displayValue}
              </Tag>
            </Tooltip>
          </div>
        );
      },
    },
  ], []);

  // Memoize row selection configuration
  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onSelect: handleSelect,
    getCheckboxProps,
    hideSelectAll: true,
  }), [selectedRowKeys, handleSelect, getCheckboxProps]);

  // Memoize expandable configuration
  const expandable = useMemo(() => ({
    expandedRowKeys,
    onExpandedRowsChange: (keys: readonly React.Key[]) => setExpandedRowKeys([...keys]),
  }), [expandedRowKeys]);

  // Memoize onRow configuration
  const onRow = useCallback((record: TransformedTreeNode) => ({
    onClick: disabled ? undefined : () => handleSelect(record, !selectedNodes.has(record.key)),
    className: getRowClassName(record),
    style: {
      cursor: disabled ? 'default' : (record.isSelectable ? 'pointer' : 'default'),
    },
  }), [disabled, handleSelect, selectedNodes, getRowClassName]);

  // Memoize dataSource array to prevent recreation
  const dataSource = useMemo(() => {
    return displayData ? [displayData] : [];
  }, [displayData]);

  return (
    <div className="netppm-tree-table" style={{ width: '100%' }}>
      {/* Search and Filter Controls */}
      <div className="tree-table-controls">
        <Input.Search
          placeholder="Search nodes..."
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={(value) => setSearchTerm(value)}
          style={{ width: 250 }}
          disabled={loading}
        />
        <span style={{ fontSize: '14px' }}>Show selected nodes only</span>
        <Badge count={selectedNodes.size} showZero={false} overflowCount={999}>
          <Switch
            checked={showSelectedOnly}
            onChange={handleToggleChange}
            disabled={loading}
          />
        </Badge>
        {showSelectedOnly && selectedNodes.size > 0 && (
          <span style={{ fontSize: '12px', opacity: 0.65 }}>
            ({selectedNodes.size} {selectedNodes.size === 1 ? 'node' : 'nodes'})
          </span>
        )}
      </div>

      {/* Table or Empty State */}
      {!displayData ? (
        showSelectedOnly ? (
          renderEmptyState()
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchTerm ? (
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>No matches found</div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    Try adjusting your search term
                  </div>
                </div>
              ) : (
                'No data'
              )
            }
          />
        )
      ) : (
        <Table<TransformedTreeNode>
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          bordered
          rowKey="key"
          rowSelection={rowSelection}
          expandable={expandable}
          onRow={onRow}
        />
      )}
    </div>
  );
};

export default TreeTable;
